import { AlertCircle, Download, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { DATASET_LABELS } from '../uploadCycle';
import type { UploadTemplatesResult } from './useUploadTemplates';

interface TemplatesCardProps {
  templates: UploadTemplatesResult;
}

/** How many columns to name before collapsing the rest into a count. */
const COLUMNS_SHOWN = 8;

/**
 * What each file has to contain, and a header row to start from.
 *
 * The columns come from the same endpoint the importer validates against, so
 * what a supplier is shown here is exactly what will be accepted.
 */
export default function TemplatesCard({ templates }: TemplatesCardProps) {
  const { templates: list, loading, error, retry, download } = templates;

  return (
    <Card data-testid="upload-templates">
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-lg">File templates</CardTitle>
        <CardDescription>The columns each file needs. Download one to start from the right header row.</CardDescription>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {error ? (
          <div className="flex flex-col items-start gap-2 rounded-lg border border-red-200 bg-red-50/60 p-3 text-sm dark:border-red-900 dark:bg-red-950/30">
            <span className="flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </span>
            <Button variant="outline" size="sm" onClick={retry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-2 rounded-lg border p-3">
                <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                <div className="h-12 w-full animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No templates are published yet. Your customer sets these up — ask them which columns to send.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {list.map(({ dataset, template }) => {
              const required = template.required_columns ?? [];
              const extra = template.columns.length - COLUMNS_SHOWN;
              return (
                <div
                  key={dataset}
                  className="flex flex-col gap-2 rounded-lg border p-3"
                  data-testid={`template-${dataset}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 font-medium text-foreground">
                        <FileSpreadsheet className="h-4 w-4 shrink-0 text-muted-foreground" />
                        {DATASET_LABELS[dataset]}
                      </p>
                      {template.description && (
                        <p className="mt-0.5 text-sm text-muted-foreground">{template.description}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => download(dataset)}
                      data-testid={`template-download-${dataset}`}
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                      Template
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {template.columns.slice(0, COLUMNS_SHOWN).map((col) => (
                      <Badge
                        key={col}
                        variant="secondary"
                        className="text-xs font-normal"
                        title={required.includes(col) ? 'Required' : 'Optional'}
                      >
                        {col}
                        {required.includes(col) ? ' *' : ''}
                      </Badge>
                    ))}
                    {extra > 0 && (
                      <Badge variant="outline" className="text-xs font-normal">
                        +{extra} more
                      </Badge>
                    )}
                  </div>
                  {required.length > 0 && <p className="text-xs text-muted-foreground">* required</p>}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
